type TopicCoverStatus = 'ready' | 'error';

interface TopicCoverRecord {
	status: TopicCoverStatus | 'loading';
	promise: Promise<TopicCoverStatus>;
}

const records = new Map<string, TopicCoverRecord>();
const assignedUrls = new WeakMap<HTMLElement, string>();

/**
 * Paint CSS background covers only after their bitmap is decoded. This avoids
 * the browser exposing a blank background between a card DOM commit and its
 * image paint, while still retaining CSS backgrounds used by existing markup.
 */
export function applyTopicCover(element: HTMLElement, url: string): Promise<void> {
	assignedUrls.set(element, url);
	element.classList.remove('topic-cover--ready', 'topic-cover--error');
	const record = getTopicCoverRecord(url);
	if (record.status === 'ready') {
		paintReadyCover(element, url);
		return Promise.resolve();
	}

	element.classList.add('topic-cover--loading');
	// A few lightweight test DOMs model style as a plain object rather than a
	// CSSStyleDeclaration. Keep the browser behavior while remaining tolerant
	// of those DOM-compatible hosts.
	if (typeof element.style.removeProperty === 'function') {
		element.style.removeProperty('background-image');
	} else {
		element.style.backgroundImage = '';
	}
	return record.promise.then((status) => {
		if (assignedUrls.get(element) !== url) return;
		element.classList.remove('topic-cover--loading');
		if (status === 'ready') paintReadyCover(element, url);
		else element.classList.add('topic-cover--error');
	});
}

function getTopicCoverRecord(url: string): TopicCoverRecord {
	const existing = records.get(url);
	if (existing) return existing;

	const record: TopicCoverRecord = {
		status: 'loading',
		promise: Promise.resolve('error'),
	};
	record.promise = decodeCover(url).then((status) => {
		record.status = status;
		// A decode rejection can be transient even when the same-origin WebP is
		// healthy (for example, an interrupted first paint). Keep this element's
		// fallback, but do not poison every later consumer of the URL.
		if (status === 'error' && records.get(url) === record) records.delete(url);
		return status;
	});
	records.set(url, record);
	return record;
}

function decodeCover(url: string): Promise<TopicCoverStatus> {
	if (typeof Image === 'undefined') return Promise.resolve('error');
	const image = new Image();
	image.decoding = 'async';
	image.src = url;
	if (typeof image.decode === 'function') {
		return image.decode().then(() => 'ready' as const, () => 'error' as const);
	}
	return new Promise((resolve) => {
		image.onload = () => resolve('ready');
		image.onerror = () => resolve('error');
	});
}

function paintReadyCover(element: HTMLElement, url: string): void {
	element.classList.remove('topic-cover--loading', 'topic-cover--error');
	element.classList.add('topic-cover--ready');
	element.style.backgroundImage = `url("${url}")`;
}

export function resetTopicCoverLoaderForTest(): void {
	records.clear();
}
